import axios from 'axios';
import type {
  AdminUpdateProviderPayoutPreferencesRequest,
  ApiResponse,
  AuditLog,
  ProviderAdminAccount,
  ProviderPayoutPreferences,
  ProviderVerification,
  ProviderVerificationStatus,
  VerificationDocumentType,
} from '@gramio/shared';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface PlatformProvider {
  providerId: string;
  userId: string;
  email?: string;
  phone?: string;
  skills: string[];
  availability: {
    status: 'available' | 'busy' | 'unavailable';
    schedule: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
    }>;
    holidays: string[];
  };
  pricing: {
    hourly?: number;
    daily?: number;
    weekly?: number;
    monthly?: number;
    yearly?: number;
    perService?: number;
    negotiable: boolean;
  };
  photos: string[];
  ratings: {
    average: number;
    count: number;
    breakdown: Record<number, number>;
  };
  location: {
    address: string;
    city: string;
    state: string;
    pincode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  serviceTypes: string[];
  certifications: Array<{
    name: string;
    issuer: string;
    issueDate: string;
    expiryDate?: string;
    documentUrl?: string;
  }>;
  experience?: {
    yearsOfExperience: number;
    description?: string;
    previousWork?: string[];
    specialties?: string[];
  };
  bio?: string;
  languages?: string[];
  personalInfo?: {
    age?: number;
    gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
    profilePhoto?: string;
    dateOfBirth?: string;
    emergencyContact?: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  professionalInfo?: {
    education?: Array<{
      degree: string;
      institution: string;
      fieldOfStudy?: string;
      graduationYear?: number;
      isVerified: boolean;
    }>;
    backgroundCheckStatus?: 'pending' | 'verified' | 'rejected' | 'not-applicable';
    backgroundCheckDate?: string;
    insuranceInfo?: {
      hasInsurance: boolean;
      insuranceProvider?: string;
      policyNumber?: string;
      expiryDate?: string;
    };
    references?: Array<{
      name: string;
      relationship: string;
      phone?: string;
      email?: string;
      company?: string;
      comments?: string;
    }>;
    awards?: Array<{
      title: string;
      issuer: string;
      date: string;
      description?: string;
    }>;
    memberships?: Array<{
      organization: string;
      membershipType: string;
      membershipNumber?: string;
      startDate: string;
      expiryDate?: string;
    }>;
    portfolio?: Array<{
      title: string;
      description?: string;
      imageUrl?: string;
      date: string;
      category?: string;
    }>;
  };
  serviceDetails?: {
    serviceRadius?: number;
    responseTime?: string;
    noticePeriod?: string;
    preferredServiceAreas?: string[];
    equipmentProvided?: string[];
    transportation?: {
      hasVehicle: boolean;
      vehicleType?: 'bike' | 'car' | 'public-transport' | 'other';
      canTransportMaterials?: boolean;
    };
    workingConditions?: {
      canWorkWeekends: boolean;
      canWorkHolidays: boolean;
      canWorkNightShifts: boolean;
      preferredShift?: 'morning' | 'afternoon' | 'evening' | 'night' | 'flexible';
    };
  };
  verification?: ProviderVerification;
  adminAccount?: ProviderAdminAccount;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderListResponse {
  providers: PlatformProvider[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface ProviderBookingsResponse {
  bookings: unknown[];
  count: number;
}

export interface ProviderEarningsResponse {
  payouts: unknown[];
  count: number;
  totalEarnings: number;
  pendingEarnings: number;
}

export interface ListProvidersParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  serviceType?: string;
  city?: string;
  state?: string;
  pincode?: string;
  verificationStatus?: ProviderVerificationStatus;
  adminAccountStatus?: 'active' | 'suspended' | 'disabled';
}

export interface ProviderAuditLogsResponse {
  logs: AuditLog[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

class ProviderManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listProviders(params?: ListProvidersParams): Promise<ProviderListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.serviceType) queryParams.append('serviceType', params.serviceType);
    if (params?.city) queryParams.append('city', params.city);
    if (params?.state) queryParams.append('state', params.state);
    if (params?.pincode) queryParams.append('pincode', params.pincode);
    if (params?.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);
    if (params?.adminAccountStatus) queryParams.append('adminAccountStatus', params.adminAccountStatus);

    const response = await this.api.get<{success: boolean; data: ProviderListResponse}>(
      `/admin/providers?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to list providers');
    }

    return response.data.data;
  }

  async getProvider(providerId: string): Promise<PlatformProvider> {
    const response = await this.api.get<{success: boolean; data: {provider: PlatformProvider}}>(
      `/admin/providers/${providerId}`
    );

    if (!response.data.success || !response.data.data?.provider) {
      throw new Error('Failed to get provider');
    }

    return response.data.data.provider;
  }

  async updateProvider(providerId: string, updates: Partial<PlatformProvider>): Promise<PlatformProvider> {
    const response = await this.api.put<{success: boolean; data: {provider: PlatformProvider}}>(
      `/admin/providers/${providerId}`,
      updates
    );

    if (!response.data.success || !response.data.data?.provider) {
      throw new Error('Failed to update provider');
    }

    return response.data.data.provider;
  }

  async postProviderVerificationUploadUrl(
    providerId: string,
    body: {contentType: string; documentType: VerificationDocumentType; skillCategoryId?: string}
  ): Promise<{uploadUrl: string; documentKey: string; documentUrl: string}> {
    try {
      const response = await this.api.post<
        ApiResponse<{uploadUrl: string; documentKey: string; documentUrl: string}>
      >(`/admin/providers/${providerId}/verification/upload-url`, body);

      if (!response.data.success || !response.data.data) {
        const msg = response.data.error?.message;
        throw new Error(msg || 'Failed to get verification upload URL');
      }
      return response.data.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
        const resBody = err.response.data as ApiResponse<null>;
        if (resBody.error?.message) throw new Error(resBody.error.message);
      }
      throw err;
    }
  }

  /**
   * Sets provider availability to unavailable (admin DELETE). Does not remove the DynamoDB record.
   */
  async deleteProvider(providerId: string): Promise<PlatformProvider> {
    try {
      const response = await this.api.delete<ApiResponse<{provider: PlatformProvider; message?: string}>>(
        `/admin/providers/${encodeURIComponent(providerId)}`
      );
      if (!response.data.success || !response.data.data?.provider) {
        const msg = response.data.error?.message;
        throw new Error(msg || 'Failed to deactivate provider');
      }
      return response.data.data.provider;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
        const b = err.response.data as ApiResponse<null>;
        if (b.error?.message) throw new Error(b.error.message);
      }
      throw err;
    }
  }

  async setProviderModeration(
    providerId: string,
    body: {action: 'suspend' | 'disable' | 'reactivate'; reason?: string}
  ): Promise<PlatformProvider> {
    try {
      const response = await this.api.post<ApiResponse<{provider: PlatformProvider}>>(
        `/admin/providers/${providerId}/moderation`,
        body
      );
      if (!response.data.success || !response.data.data?.provider) {
        const msg = response.data.error?.message;
        throw new Error(msg || 'Failed to update moderation');
      }
      return response.data.data.provider;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
        const b = err.response.data as ApiResponse<null>;
        if (b.error?.message) throw new Error(b.error.message);
      }
      throw err;
    }
  }

  async listProviderAuditLogs(
    providerId: string,
    params?: {limit?: number; lastEvaluatedKey?: string}
  ): Promise<ProviderAuditLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    const q = queryParams.toString();
    const response = await this.api.get<{
      success: boolean;
      data: ProviderAuditLogsResponse;
    }>(`/admin/providers/${providerId}/audit-logs${q ? `?${q}` : ''}`);

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to load audit logs');
    }
    return response.data.data;
  }

  async verifyProvider(providerId: string): Promise<PlatformProvider> {
    try {
      const response = await this.api.post<ApiResponse<{provider: PlatformProvider}>>(
        `/admin/providers/${providerId}/verify`
      );

      if (!response.data.success || !response.data.data?.provider) {
        const msg = response.data.error?.message;
        throw new Error(msg || 'Failed to verify provider');
      }

      return response.data.data.provider;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
        const body = err.response.data as ApiResponse<null>;
        if (body.error?.message) throw new Error(body.error.message);
      }
      throw err;
    }
  }

  async getProviderBookings(providerId: string): Promise<ProviderBookingsResponse> {
    const response = await this.api.get<{success: boolean; data: ProviderBookingsResponse}>(
      `/admin/providers/${providerId}/bookings`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get provider bookings');
    }

    return response.data.data;
  }

  async getProviderEarnings(providerId: string): Promise<ProviderEarningsResponse> {
    const response = await this.api.get<{success: boolean; data: ProviderEarningsResponse}>(
      `/admin/providers/${providerId}/earnings`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get provider earnings');
    }

    return response.data.data;
  }

  async getProviderPayoutPreferences(providerId: string): Promise<ProviderPayoutPreferences | null> {
    const response = await this.api.get<{
      success: boolean;
      data: { preferences: ProviderPayoutPreferences | null };
    }>(`/admin/providers/${providerId}/payout-preferences`);

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get provider payout preferences');
    }

    return response.data.data.preferences;
  }

  async updateProviderPayoutPreferences(
    providerId: string,
    body: AdminUpdateProviderPayoutPreferencesRequest
  ): Promise<ProviderPayoutPreferences | null> {
    const response = await this.api.put<{
      success: boolean;
      data: { preferences: ProviderPayoutPreferences | null };
    }>(`/admin/providers/${providerId}/payout-preferences`, body);

    if (!response.data.success || response.data.data.preferences == null) {
      throw new Error('Failed to update payout preferences');
    }

    return response.data.data.preferences;
  }
}

export const providerManagementService = new ProviderManagementService();

